<template>
  <v-dialog v-model="display" width="62%" persistent>
    <v-card v-if="isLoading">
      <div class="d-flex justify-center align-center" style="height: 100vh;">
        <v-progress-circular
          indeterminate
          color="#2661FF"
          size="80"
        ></v-progress-circular>
      </div>
    </v-card>
    <v-card v-else class="rounded-lg grey lighten-4">
      <v-card-title class="headline pa-6 justify-center white">
        {{ $t("pricing_model.edit.add_currency.title") }}
      </v-card-title>
      <v-card-text class="text-center white">
        <span class="subtitle-1 grey--text text--darken-3">
          {{ $t("pricing_model.edit.add_currency.description") }}
        </span>
      </v-card-text>
      <v-divider class="mx-12"></v-divider>

      <v-card-text class="mt-6">
        <!------------------------------------------>
        <!-- CURRRENCY ----------------------------->
        <!------------------------------------------>
        <v-card
          class="rounded-xl white pa-4"
          flat
          style="border: 1px solid #BCBCBC !important;"
        >
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <p
                  class="font-weight-regular d-flex justify-center mt-2"
                  style="font-size: 18px;"
                >
                  {{ $t("pricing_model.edit.add_currency.select_currency") }}
                </p>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="selected_currency"
                  :items="filteredCurrencies"
                  item-text="name"
                  item-value="id"
                  class="rounded-lg"
                  :label="$t('pricing_model.create.step_2.currency.title')"
                  hide-details
                  dense
                  outlined
                  @change="resetPlan"
                  clearable
                >
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <div>
          <v-card
            v-if="lastSavedPlan && lastSavedPlan.hasNoLimits"
            class="mt-8 rounded-xl pa-2 mt-4"
            flat
            style="border: 1px solid #BCBCBC !important;"
          >
            <v-card-text>
              <p
                class="subtitle-1 font-weight-medium text-center"
                style="color:#2661ff"
              >
                {{
                  $t("pricing_model.edit.add_currency.plan_without_limit.title")
                }}
              </p>
              <p class="subtitle-2 text-center">
                {{
                  $t(
                    "pricing_model.edit.add_currency.plan_without_limit.description"
                  )
                }}
              </p>
            </v-card-text>
          </v-card>
          <v-card
            class="rounded-xl pa-2 mt-4"
            v-else-if="selected_currency"
            flat
            style="border: 1px solid #BCBCBC;"
          >
            <v-card-text>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row class="my-4">
                  <!------------------------------------------>
                  <!-- NAME ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" md="5">
                    <span class="subtitle-2">{{ inputs.name.title }}</span>
                    <v-text-field
                      v-model="plan.name"
                      class="rounded-lg mt-1"
                      :placeholder="inputs.name.placeholder"
                      outlined
                      dense
                      required
                      :rules="inputs.name.rules"
                      ><v-tooltip slot="append" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="grey lighten-1" size="22"
                            >help</v-icon
                          >
                        </template>
                        <span style="max-width: 200px;">{{
                          inputs.name.tooltip
                        }}</span>
                      </v-tooltip>
                    </v-text-field>
                  </v-col>
                  <!------------------------------------------>
                  <!-- FREQUENCY ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="6" md="3" class="text-center ml-4">
                    <span class="subtitle-2">{{ inputs.frequency.title }}</span>
                    {{ plan.frequency }}
                    <v-select
                      v-if="pricingModel.frequency === 3"
                      v-model="plan.frequency"
                      :items="frequencies"
                      :placeholder="inputs.frequency.title"
                      item-text="title"
                      item-value="value"
                      class="rounded-lg mt-1"
                      :rules="inputs.frequency.rules"
                      outlined
                      dense
                    ></v-select>

                    <div v-else>
                      <p class="subtitle-2 font-weight-regular mt-3">
                        {{ getFrequencyName(plan.frequency) }}
                      </p>
                    </div>
                  </v-col>
                  <!------------------------------------------>
                  <!-- COLOR ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="6" md="3" class="color-picker-column">
                    <span
                      class="subtitle-2 d-flex justify-center align-center"
                      >{{ inputs.color.title }}</span
                    >
                    <div
                      class="d-flex justify-center align-center mt-2"
                      style="gap: 8px;"
                    >
                      <v-menu offset-y>
                        <template v-slot:activator="{ on }">
                          <div v-on="on">
                            <v-icon v-if="!plan.color" color="grey"
                              >palette</v-icon
                            >
                            <div
                              v-if="plan.color"
                              class="color-swatch"
                              :style="{ backgroundColor: plan.color.hex }"
                            ></div>
                          </div>
                        </template>

                        <v-color-picker
                          v-model="plan.color"
                          dot-size="25"
                          hide-canvas
                          hide-inputs
                          hide-mode-switch
                          hide-sliders
                          show-swatches
                          swatches-max-height="200"
                        ></v-color-picker>
                      </v-menu>
                    </div>
                  </v-col>
                  <!------------------------------------------>
                  <!-- DESCRIPTION ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" md="12">
                    <span class="subtitle-2">{{
                      inputs.description.title
                    }}</span>
                    <v-text-field
                      v-model="plan.description"
                      :placeholder="inputs.description.placeholder"
                      class="rounded-lg mt-1"
                      outlined
                      dense
                      :rules="inputs.description.rules"
                      required
                    >
                      <v-tooltip slot="append" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="grey lighten-1" size="22"
                            >help</v-icon
                          >
                        </template>
                        <span style="max-width: 200px;">{{
                          inputs.description.tooltip
                        }}</span>
                      </v-tooltip>
                    </v-text-field>
                  </v-col>
                  <!------------------------------------------>
                  <!-- AMOUNT ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <span class="subtitle-2"
                        >{{ inputs.amount.title }}
                        <v-tooltip slot="append" bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" color="grey lighten-1" size="18"
                              >help</v-icon
                            >
                          </template>
                          <span style="max-width: 200px;">
                            {{ inputs.amount.tooltip }}
                          </span>
                        </v-tooltip>
                      </span>
                      <v-switch
                        v-model="plan.hasCustomAmount"
                        :disabled="pricingModel.has_limits_table"
                        :label="inputs.amount.switch"
                        color="#2661FF"
                        inset
                        class="ml-6"
                        @change="customAmount(plan)"
                      ></v-switch>
                    </div>
                    <div
                      v-if="!plan.hasCustomAmount"
                      class="d-flex justify-center align-center"
                    >
                      <span style="font-size: 12px;" class="mb-4 mr-4">
                        {{
                          `${getCurrencyCode(selected_currency).code} (${
                            getCurrencyCode(selected_currency).symbol
                          })`
                        }}
                      </span>
                      <v-text-field
                        v-if="!plan.hasCustomAmount"
                        v-model="plan.amount"
                        :placeholder="inputs.amount.placeholder"
                        class="rounded-lg mt-1"
                        outlined
                        dense
                        :disabled="plan.hasCustomAmount"
                        :rules="
                          !plan.hasCustomAmount ? inputs.amount.rules : ''
                        "
                        required
                      ></v-text-field>
                    </div>
                  </v-col>
                  <!------------------------------------------>
                  <!-- FEE_PERCENTAGE ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <span class="subtitle-2"
                        >{{ inputs.fee_percentage.title }}
                        <v-tooltip slot="append" bottom>
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" color="grey lighten-1" size="18"
                              >help</v-icon
                            >
                          </template>
                          <span style="max-width: 200px;">
                            {{ inputs.fee_percentage.tooltip }}
                          </span>
                        </v-tooltip></span
                      >
                      <v-switch
                        v-model="plan.hasCustomFee"
                        :disabled="pricingModel.has_limits_table"
                        :label="inputs.fee_percentage.switch"
                        color="#2661FF"
                        inset
                        class="ml-6"
                        @change="customFee(plan)"
                      ></v-switch>
                    </div>
                    <div
                      v-if="!plan.hasCustomFee"
                      class="d-flex justify-center align-center"
                    >
                      <span class="mb-4 mr-4">
                        %
                      </span>
                      <v-text-field
                        v-if="!plan.hasCustomFee"
                        v-model="plan.fee_percentage"
                        :placeholder="inputs.fee_percentage.placeholder"
                        class="rounded-lg mt-1"
                        outlined
                        dense
                        :rules="inputs.fee_percentage.rules"
                        required
                      ></v-text-field>
                    </div>
                  </v-col>
                  <!------------------------------------------>
                  <!-- LIMITS TABLE ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12">
                    <v-row v-if="pricingModel.has_limits_table">
                      <!------------------------------------------>
                      <!-- LIMIT REGISTERS ----------------------------->
                      <!------------------------------------------>
                      <v-col cols="12">
                        <div class="d-flex align-center">
                          <span class="subtitle-2"
                            >{{ inputs.registers_table.title }}
                            <v-tooltip slot="append" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  color="grey lighten-1"
                                  size="18"
                                  >help</v-icon
                                >
                              </template>
                              <span style="max-width: 200px;">
                                {{ inputs.registers_table.tooltip }}
                              </span>
                            </v-tooltip>
                          </span>
                          <!------------------------------------------>
                          <!-- SWITCH NO LIMIT ----------------------------->
                          <!------------------------------------------>
                          <v-switch
                            :disabled="lastHasNoLimit"
                            label="Último límite"
                            color="#2661FF"
                            inset
                            class="ml-6"
                            @change="toggleNoLimit(plan)"
                          ></v-switch>
                        </div>
                        <v-row>
                          <v-col cols="12" md="6" class="mt-4 d-flex">
                            <span class="mr-4 mt-3">{{
                              inputs.registers_table.from
                            }}</span>
                            <v-text-field
                              v-model="plan.registers_offset"
                              placeholder="Sin límite"
                              disabled
                              class="rounded-lg mt-1"
                              outlined
                              dense
                              :rules="inputs.registers_table.rules"
                              required
                            ></v-text-field>
                          </v-col>
                          <v-col
                            cols="12"
                            md="6"
                            class="mt-4 d-flex"
                            v-if="validateNoLimits(plan.hasNoLimits)"
                          >
                            <span class="mr-4 mt-3">{{
                              inputs.registers_table.to
                            }}</span>
                            <v-text-field
                              v-model="plan.registers_limit"
                              :placeholder="inputs.registers_table.placeholder"
                              class="rounded-lg mt-1"
                              outlined
                              type="number"
                              dense
                              :disabled="!validateNoLimits(plan.hasNoLimits)"
                              :rules="[
                                validateLimit(
                                  plan.registers_limit,
                                  'registers_offset'
                                ),
                                v =>
                                  (v && v > 0) ||
                                  this.$t(
                                    'pricing_model.create.step_2.amount.rules.min'
                                  )
                              ]"
                              required
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>
                      <!------------------------------------------>
                      <!-- LIMIT INCOMES ----------------------------->
                      <!------------------------------------------>
                      <v-col cols="12">
                        <div class="d-flex align-center">
                          <span class="subtitle-2"
                            >{{ inputs.incomes_table.title }}
                            <v-tooltip slot="append" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  color="grey lighten-1"
                                  size="18"
                                  >help</v-icon
                                >
                              </template>
                              <span style="max-width: 200px;">
                                {{ inputs.incomes_table.tooltip }}
                              </span>
                            </v-tooltip></span
                          >
                        </div>
                        <v-row>
                          <v-col cols="12" md="6" class="mt-4 d-flex">
                            <span class="mr-4 mt-3">{{
                              inputs.incomes_table.from
                            }}</span>
                            <v-text-field
                              v-model="plan.incomes_offset"
                              placeholder="Sin límite"
                              disabled
                              class="rounded-lg mt-1"
                              :label="
                                `${getCurrencyCode(selected_currency).code} (${
                                  getCurrencyCode(selected_currency).symbol
                                })`
                              "
                              outlined
                              dense
                              :rules="inputs.incomes_table.rules"
                              required
                            ></v-text-field>
                          </v-col>
                          <v-col
                            cols="12"
                            md="6"
                            class="mt-4 d-flex"
                            v-if="validateNoLimits(plan.hasNoLimits)"
                          >
                            <span class="mr-4 mt-3">{{
                              inputs.incomes_table.to
                            }}</span>
                            <v-text-field
                              v-model="plan.incomes_limit"
                              :placeholder="inputs.incomes_table.placeholder"
                              :label="
                                `${getCurrencyCode(selected_currency).code} (${
                                  getCurrencyCode(selected_currency).symbol
                                })`
                              "
                              class="rounded-lg mt-1"
                              outlined
                              type="number"
                              dense
                              :disabled="!validateNoLimits(plan.hasNoLimits)"
                              required
                              :rules="[
                                validateLimit(
                                  plan.incomes_limit,
                                  'incomes_offset'
                                ),
                                v =>
                                  (v && v > 0) ||
                                  this.$t(
                                    'pricing_model.create.step_2.amount.rules.min'
                                  )
                              ]"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                  <!------------------------------------------>
                  <!-- ADDITIONAL SERVICES ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12">
                    <p class="subtitle-2">
                      {{ inputs.additional_services.title }}
                      <v-tooltip slot="append" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="grey lighten-1" size="18"
                            >help</v-icon
                          >
                        </template>
                        <span style="max-width: 200px;">
                          {{ inputs.additional_services.tooltip }}
                        </span>
                      </v-tooltip>
                    </p>

                    <v-menu
                      ref="menu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          class="rounded-xl"
                          small
                          outlined
                          color="#2661FF"
                          v-on="on"
                        >
                          <v-icon>add</v-icon>
                          {{ $t("general.add") }}
                        </v-btn>
                      </template>
                      <v-card class="rounded-lg" flat width="250">
                        <v-list dense>
                          <v-list-item-group
                            v-model="plan.selected_services"
                            multiple
                            color="#2661FF"
                          >
                            <v-list-item
                              v-for="(item, i) in additionalServices"
                              :key="i"
                              @click="toggleService(item, plan)"
                            >
                              <template v-slot:default="{ active }">
                                <v-list-item-action>
                                  <v-checkbox
                                    :input-value="active"
                                    color="#2661FF"
                                  ></v-checkbox>
                                </v-list-item-action>
                                <v-list-item-content>
                                  <v-list-item-title class="subtitle-2">
                                    {{
                                      item.name.charAt(0).toUpperCase() +
                                        item.name.slice(1)
                                    }}
                                  </v-list-item-title>
                                </v-list-item-content>
                              </template>
                            </v-list-item>
                          </v-list-item-group>
                        </v-list>
                      </v-card>
                    </v-menu>
                    <v-row>
                      <v-col
                        cols="12"
                        md="4"
                        class="mt-4"
                        v-for="(service,
                        service_index) in plan.additional_services"
                        :key="service_index"
                      >
                        <div>
                          <span class="subtitle-2">
                            {{
                              service.name.charAt(0).toUpperCase() +
                                service.name.slice(1)
                            }}
                          </span>
                          <v-switch
                            v-model="service.hasCustomService"
                            :label="
                              `${service.name} ${inputs.additional_services.switch}`
                            "
                            color="#2661FF"
                            inset
                            class="ml-6"
                            @change="customService(plan, service_index)"
                          ></v-switch>
                        </div>
                        <v-text-field
                          v-if="
                            plan.additional_services &&
                              !service.hasCustomService
                          "
                          v-model="service.amount_per_action"
                          :placeholder="inputs.additional_services.placeholder"
                          class="rounded-lg"
                          outlined
                          dense
                          :rules="inputs.additional_services.rules"
                          required
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                  <!------------------------------------------>
                  <!-- ACTIONS ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" class="mt-12 d-flex justify-end">
                    <v-btn class="rounded-lg" color="#2661FF" @click="savePlan">
                      <v-icon color="white" class="mr-2">
                        save
                      </v-icon>
                      <span style="color: white;">
                        {{ `${$t("actions.save")} Plan` }}
                      </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-card-text v-if="Object.keys(savedPlans).length > 0">
        <v-row>
          <v-col cols="12">
            <template>
              <v-expansion-panels>
                <ShowCurrencyPlan
                  v-for="(item, i) in savedPlans"
                  :key="i"
                  :plan="item"
                  :savedPlans="savedPlans"
                  class="rounded-lg my-2"
                  @delete-plan="removePlan(i)"
                />
              </v-expansion-panels>
            </template>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn outlined color="#2661FF" @click="$emit('close-dialog')">{{
          $t("general.close")
        }}</v-btn>
        <v-btn
          color="#2661FF"
          :disabled="
            Object.keys(savedPlans).length === 0 || lastSavedPlanWithoutLimits()
          "
          @click="addCurrency"
          :loading="addCurrencyLoading"
        >
          <v-icon color="white">add</v-icon>
          <span style="color: white;">
            {{
              `${$t("actions.add")} ${$t(
                "pricing_model.create.step_2.currency.title"
              )}`
            }}</span
          ></v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from "vuex";
import ShowCurrencyPlan from "@/components/PricingModels/Edit/AddCurrency/ShowCurrencyPlan";

export default {
  props: {
    display: { type: Boolean, required: true }
  },
  components: {
    ShowCurrencyPlan
  },
  data() {
    return {
      isLoading: false,
      addCurrencyLoading: false,
      valid: true,
      selected_currency: null,
      plan: {
        name: null,
        frequency: null,
        color: null,
        description: null,
        hasNoLimits: false,
        amount: null,
        fee_percentage: null,
        registers_offset: null,
        registers_limit: null,
        incomes_offset: null,
        incomes_limit: null,
        hasCustomAmount: false,
        hasCustomFee: false,
        additional_services: [],
        selected_services: []
      },
      inputs: {
        name: {
          title: this.$t("pricing_model.create.step_2.name.title"),
          placeholder: this.$t("pricing_model.create.step_2.name.title"),
          tooltip: this.$t("pricing_model.create.step_2.name.tooltip"),
          rules: [
            v => !!v || this.$t("pricing_model.create.step_2.name.rule"),
            v => {
              const existingPlan = this.savedPlans.find(
                plan => plan.name === v
              );
              return (
                !existingPlan ||
                this.$t("pricing_model.create.step_2.name.error")
              );
            }
          ]
        },
        frequency: {
          title: this.$t("pricing_model.create.step_2.frequency.title"),
          rules: [
            v => !!v || this.$t("pricing_model.create.step_2.frequency.rule")
          ]
        },
        color: {
          title: this.$t("pricing_model.create.step_2.color.title")
        },
        description: {
          title: this.$t("pricing_model.create.step_2.description.title"),
          placeholder: this.$t("pricing_model.create.step_2.description.title"),
          tooltip: this.$t("pricing_model.create.step_2.description.tooltip"),
          rules: [
            v =>
              (!!v && v.length > 10) ||
              this.$t("pricing_model.create.step_2.description.rule")
          ]
        },
        amount: {
          title: this.$t("pricing_model.create.step_2.amount.title"),
          placeholder: this.$t(
            "pricing_model.create.step_2.amount.placeholder"
          ),
          switch: this.$t("pricing_model.create.step_2.amount.switch"),
          tooltip: this.$t("pricing_model.create.step_2.amount.tooltip"),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t("pricing_model.create.step_2.amount.rules.number"),
            v =>
              (v && v.toString().split(".").length < 2) ||
              (v && v.toString().split(".")[1].length <= 2) ||
              this.$t("pricing_model.create.step_2.amount.rules.decimal"),
            v =>
              (v && v >= 0) ||
              this.$t("pricing_model.create.step_2.amount.rules.min")
          ]
        },
        fee_percentage: {
          title: this.$t("pricing_model.create.step_2.fee_percentage.title"),
          placeholder: this.$t(
            "pricing_model.create.step_2.fee_percentage.placeholder"
          ),
          switch: this.$t("pricing_model.create.step_2.fee_percentage.switch"),
          tooltip: this.$t(
            "pricing_model.create.step_2.fee_percentage.tooltip"
          ),
          rules: [
            v =>
              !!v ||
              this.$t(
                "pricing_model.create.step_2.fee_percentage.rules.required"
              ),
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              this.$t(
                "pricing_model.create.step_2.fee_percentage.rules.number"
              ),
            v =>
              (v && v.toString().split(".").length < 2) ||
              (v && v.toString().split(".")[1].length <= 2) ||
              this.$t(
                "pricing_model.create.step_2.fee_percentage.rules.decimal"
              ),
            v =>
              (v && v >= 0) ||
              this.$t("pricing_model.create.step_2.fee_percentage.rules.min"),
            v =>
              (v && v <= 100) ||
              this.$t("pricing_model.create.step_2.fee_percentage.rules.max")
          ]
        },
        registers_table: {
          title: this.$t("pricing_model.create.step_2.registers.title"),
          from: this.$t("pricing_model.create.step_2.registers.from"),
          to: this.$t("pricing_model.create.step_2.registers.to"),
          tooltip: this.$t("pricing_model.create.step_2.registers.tooltip"),
          placeholder: this.$t(
            "pricing_model.create.step_2.registers.placeholder"
          ),
          rules: [
            v =>
              !!v ||
              /^\d+$/.test(v) ||
              this.$t("pricing_model.create.step_2.registers.rule")
          ]
        },
        incomes_table: {
          title: this.$t("pricing_model.create.step_2.incomes.title"),
          from: this.$t("pricing_model.create.step_2.incomes.from"),
          to: this.$t("pricing_model.create.step_2.incomes.to"),
          tooltip: this.$t("pricing_model.create.step_2.incomes.tooltip"),
          placeholder: this.$t(
            "pricing_model.create.step_2.incomes.placeholder"
          ),
          rules: [
            v =>
              !!v ||
              /^\d+$/.test(v) ||
              this.$t("pricing_model.create.step_2.incomes.rule")
          ]
        },
        additional_services: {
          title: this.$t("pricing_model.create.step_2.services.title"),
          tooltip: this.$t("pricing_model.create.step_2.services.tooltip"),
          placeholder: this.$t(
            "pricing_model.create.step_2.services.placeholder"
          ),
          switch: this.$t("pricing_model.create.step_2.services.switch"),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t("pricing_model.create.step_2.services.rule.number"),
            v =>
              (v && v.toString().split(".").length < 2) ||
              (v && v.toString().split(".")[1].length <= 6) ||
              this.$t("pricing_model.create.step_2.services.rule.decimal"),
            v =>
              (v && v >= 0) ||
              this.$t("pricing_model.create.step_2.services.rule.min")
          ]
        }
      },
      frequencies: [
        {
          title: this.$t("pricing_model.create.step_1.frequency.monthly"),
          value: 1
        },
        {
          title: this.$t("pricing_model.create.step_1.frequency.yearly"),
          value: 2
        }
      ],
      savedPlans: []
    };
  },
  async created() {
    this.isLoading = true;
    try {
      const { pricingModel, plan } = this;
      this.setFrequencies();
      await this.getAdditionalServices();

      // Validate if pricingModel is not empty and if has_limits_table
      const setFirstOffsets =
        pricingModel &&
        Object.keys(pricingModel).length > 0 &&
        pricingModel.has_limits_table &&
        this.savedPlans.length === 0;

      if (setFirstOffsets) {
        plan.registers_offset = 0;
        plan.incomes_offset = 0;
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    ...mapState({
      currencies: ({ countries: { currencies } }) => currencies,
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel,
      additionalServices: ({ pricingModels: { additionalServices } }) =>
        additionalServices
    }),
    filteredCurrencies() {
      const { currencies } = this.pricingModel;

      return this.currencies.filter(curr => {
        // Check if the currency's id exists in the pricingModel currencies
        return !currencies.some(pricingCurr => pricingCurr.id === curr.id);
      });
    },
    lastHasNoLimit() {
      // Disable limits switch if the last plan has no limit
      let lastPlan = this.savedPlans[this.savedPlans.length - 1];
      if (lastPlan && lastPlan.hasNoLimits) {
        return true;
      } else {
        return false;
      }
    },
    lastSavedPlan() {
      return this.savedPlans[this.savedPlans.length - 1];
    }
  },
  methods: {
    ...mapActions({
      getAdditionalServices: "pricingModels/getAdditionalServices",
      addPricingModelCurrency: "pricingModels/addPricingModelCurrency"
    }),
    setFrequencies() {
      const [MONTHLY, YEARLY] = [1, 2];
      if ([MONTHLY, YEARLY].includes(this.pricingModel.frequency)) {
        this.plan.frequency = this.pricingModel.frequency;
      }
    },
    getFrequencyName(frequency) {
      const matchedFrequency = this.frequencies.find(
        freq => freq.value === frequency
      );
      return matchedFrequency
        ? matchedFrequency.title
        : this.$t("pricing_model.create.step_2.frequency.unknown_frequency");
    },
    toggleNoLimit(plan) {
      this.$set(plan, "hasNoLimits", !plan.hasNoLimits);
      if (plan.hasNoLimits) {
        this.$set(plan, "registers_limit", null);
        this.$set(plan, "incomes_limit", null);
      }
    },
    customAmount(plan) {
      if (plan.hasCustomAmount) {
        this.$set(plan, "amount", null);
      }
    },
    customFee(plan) {
      if (plan.hasCustomFee) {
        this.$set(plan, "fee_percentage", null);
      }
    },
    getCurrencyCode(currency_id) {
      const currency = this.currencies.find(
        currency => currency.id === currency_id
      );
      return currency;
    },
    validateNoLimits(value) {
      let lastPlan = this.savedPlans[this.savedPlans.length - 1];
      // Find if the last plan has no limits so the table is finished
      if (value || (lastPlan && lastPlan.hasNoLimits)) {
        return false;
      }
      return true;
    },
    validateLimit(value, offset) {
      if (value === null) {
        return true; // Allow null
      }

      const limit = parseInt(value, 10);
      const offsetValue = parseInt(this.plan[offset], 10);

      return (
        limit > offsetValue ||
        `${this.$t("pricing_model.edit.add_currency.limit_greater")} ${offset}`
      );
    },
    toggleService(item, plan) {
      const index = plan.additional_services.findIndex(
        selectedItem => selectedItem.name === item.name
      );
      if (index !== -1) {
        plan.additional_services.splice(index, 1); // Item exists, remove it
      } else {
        // Service doesn't exist, add it
        const newService = {
          ...item, // Copy properties from item
          active: true, // Set initial active state as true or false as needed
          amount_per_action: null // Modify as needed
        };
        this.$set(
          plan.additional_services,
          plan.additional_services.length,
          newService
        );
      }
    },
    customService(plan, service_index) {
      const service = plan.additional_services[service_index];
      if (service.hasCustomService) {
        this.$set(service, "amount_per_action", null);
      }
    },
    getOffset(plan, property) {
      let lastPlan = this.savedPlans[this.savedPlans.length - 1];

      const propertyOffset =
        property === "registers" ? "registers_offset" : "incomes_offset";
      const propertyLimit =
        property === "registers" ? "registers_limit" : "incomes_limit";

      plan[propertyOffset] = lastPlan[propertyLimit];
    },
    lastSavedPlanWithoutLimits() {
      const lastSavedPlan = this.savedPlans[this.savedPlans.length - 1];
      return this.pricingModel.has_limits_table && !lastSavedPlan.hasNoLimits;
    },
    resetPlan() {
      this.plan = {
        name: null,
        frequency: null,
        color: null,
        description: null,
        hasNoLimits: false,
        amount: null,
        fee_percentage: null,
        registers_offset: this.pricingModel.has_limits_table ? 0 : null,
        registers_limit: null,
        incomes_offset: this.pricingModel.has_limits_table ? 0 : null,
        incomes_limit: null,
        hasCustomAmount: false,
        hasCustomFee: false,
        additional_services: [],
        selected_services: []
      };
      this.savedPlans = [];

      this.setFrequencies();
    },
    async resetForm() {
      await this.$refs.form.reset();
    },
    async savePlan() {
      try {
        if (await this.$refs.form.validate()) {
          // Create a copy of this.plan
          const planCopy = JSON.parse(JSON.stringify(this.plan));

          await this.resetForm();
          this.plan = {
            name: null,
            frequency: null,
            color: null,
            description: null,
            hasNoLimits: false,
            amount: null,
            fee_percentage: null,
            registers_offset: null,
            registers_limit: null,
            incomes_offset: null,
            incomes_limit: null,
            hasCustomAmount: false,
            hasCustomFee: false,
            additional_services: [],
            selected_services: []
          };

          this.savedPlans.push({
            ...planCopy,
            currency_id: this.selected_currency
          });
          this.setFrequencies();
          this.getOffset(this.plan, "registers");
          this.getOffset(this.plan, "incomes");

          this.$noty.success(
            this.$t("pricing_model.messages.saved_plan_success")
          );
        } else {
          this.$noty.error(this.$t("pricing_model.messages.verifiy_data"));
        }
      } catch (error) {
        console.log(error);
        this.$noty.error(this.$t("pricing_model.messages.saved_plan_error"));
      }
    },
    removePlan(index) {
      if (index >= 0 && index < this.savedPlans.length) {
        const removedPlan = this.savedPlans.splice(index, 1);
        if (this.pricingModel.has_limits_table) {
          this.plan.registers_offset = removedPlan[0].registers_offset;
          this.plan.incomes_offset = removedPlan[0].incomes_offset;
        }
      }
    },
    async addCurrency() {
      try {
        this.addCurrencyLoading = true;
        const currency = this.selected_currency;
        const pricing_model_id = this.pricingModel.id;

        const payload = [[...this.savedPlans], currency, pricing_model_id];

        await this.addPricingModelCurrency(payload);
      } catch (error) {
        console.log(error);
      } finally {
        this.addCurrencyLoading = false;
        this.$emit("close-dialog");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.color-swatch {
  cursor: pointer;
  height: 25px;
  width: 25px;
  border: 2px solid #bdbdbd; /* Border styling */
  border-radius: 20px; /* To make it a circle */
  transition: border-radius 200ms ease-in-out;
}
</style>
